import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthContext';
import {
  useCreateUser,
  useDeleteUser,
  useResetPassword,
  useUpdateUser,
  useUsers,
} from '@/features/users/hooks';
import type { Role, User } from '@/types';
import { initials } from '@/lib/format';
import { AppLayout } from '@/components/AppLayout';
import { Sheet } from '@/components/ui/Sheet';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { Icon } from '@/components/ui/Icon';
import { Spinner } from '@/components/ui/Spinner';
import { Toggle } from '@/components/ui/Toggle';
import { useToast } from '@/components/ui/Toast';

const ROLES: Role[] = ['Administrador', 'Encarregado', 'Eletricista'];

/** Administrador-only: create the two admin-level accounts and the eletricistas. */
export function UtilizadoresPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { user: me } = useAuth();

  const users = useUsers();
  const criar = useCreateUser();
  const alterar = useUpdateUser();
  const reset = useResetPassword();
  const remover = useDeleteUser();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editar, setEditar] = useState<User | null>(null);

  const emitirCodigo = async (user: User) => {
    try {
      const { codigo } = await reset.mutateAsync({ id: user.id });
      toast.show(t('users.resetFeito', { nome: user.displayName, codigo }));
    } catch (err) {
      toast.show(err instanceof Error ? err.message : t('common.error'));
    }
  };

  const apagar = async (user: User) => {
    if (!window.confirm(t('users.confirmarRemocao', { nome: user.displayName }))) return;
    try {
      await remover.mutateAsync(user.id);
      toast.show(t('users.removido'));
    } catch (err) {
      toast.show(err instanceof Error ? err.message : t('common.error'));
    }
  };

  return (
    <AppLayout title={t('users.titulo')} subtitle={t('perfil.utilizadoresSub')}>
      <div className="flex flex-col gap-4">
        <button
          type="button"
          onClick={() => navigate('/perfil')}
          className="flex items-center gap-1.5 self-start rounded-full border border-conduril-border bg-white py-2 pl-2.5 pr-3.5 text-[11px] font-semibold text-conduril"
        >
          <Icon name="arrow_back" className="text-[16px]" /> {t('header.perfil')}
        </button>

        {users.isLoading && (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        )}
        {users.error && <ErrorBanner error={users.error} onRetry={() => void users.refetch()} />}
        {users.data?.length === 0 && <EmptyState message={t('mapa.semDados')} />}

        <ul className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {(users.data ?? []).map((user) => (
            <li key={user.id} className="card p-3.5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#eef0f8] text-[11px] font-semibold text-conduril">
                    {initials(user.displayName)}
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-[14px] font-semibold leading-tight text-conduril-ink">
                      {user.displayName}
                    </div>
                    <div className="num mt-1 truncate text-[10px] tracking-wide text-conduril-muted">
                      {user.username} · {t(`users.role${user.role}`)}
                      {user.numeroMecanografico ? ` · ${user.numeroMecanografico}` : ''}
                    </div>
                  </div>
                </div>
                <span
                  className={`shrink-0 rounded-full px-2.5 py-1 text-[9px] font-semibold uppercase tracking-wide ${
                    user.ativo ? 'bg-ok-bg text-ok-text' : 'bg-[#f0f1f6] text-conduril-muted'
                  }`}
                >
                  {user.ativo ? t('users.ativo') : t('users.inativo')}
                </span>
              </div>

              <div className="mt-3 flex flex-wrap gap-2 border-t border-[#eef0f5] pt-3">
                <button
                  type="button"
                  onClick={() => {
                    setEditar(user);
                    setSheetOpen(true);
                  }}
                  className="rounded-lg border border-conduril-border px-3 py-1.5 text-[11px] font-semibold text-conduril"
                >
                  {t('common.edit')}
                </button>
                <button
                  type="button"
                  onClick={() => void emitirCodigo(user)}
                  disabled={reset.isPending}
                  className="rounded-lg border border-conduril-border px-3 py-1.5 text-[11px] font-semibold text-conduril-muted"
                >
                  {t('users.resetCodigo')}
                </button>
                {user.id !== me?.id && (
                  <button
                    type="button"
                    onClick={() => void apagar(user)}
                    className="rounded-lg border border-danger-border px-3 py-1.5 text-[11px] font-semibold text-danger-text"
                  >
                    {t('common.delete')}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={() => {
            setEditar(null);
            setSheetOpen(true);
          }}
          className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-[#b9c0d2] bg-white px-4 py-3.5 text-sm font-semibold text-conduril md:w-auto md:self-start md:px-6"
        >
          <Icon name="person_add" className="text-[19px]" /> {t('users.novo')}
        </button>
      </div>

      <UserSheet
        open={sheetOpen}
        user={editar}
        busy={criar.isPending || alterar.isPending}
        onClose={() => setSheetOpen(false)}
        onSave={(input) => {
          void (async () => {
            try {
              if (editar) {
                const { password: _password, username: _username, ...patch } = input;
                await alterar.mutateAsync({ id: editar.id, ...patch });
                toast.show(t('users.atualizado'));
              } else {
                // A blank código still has to be something the worker can type in;
                // the account is flagged must-change either way.
                await criar.mutateAsync({ ...input, password: input.password || '1234' });
                toast.show(t('users.criado', { nome: input.displayName }));
              }
              setSheetOpen(false);
            } catch (err) {
              toast.show(err instanceof Error ? err.message : t('common.error'));
            }
          })();
        }}
      />
    </AppLayout>
  );
}

interface UserForm {
  displayName: string;
  username: string;
  password?: string;
  role: Role;
  numeroMecanografico: string | null;
  categoria: string | null;
  email: string | null;
  phoneNumber: string | null;
  naEmpresaDesde: string | null;
  horasAlvoDiarias: number | null;
  ativo: boolean;
}

function UserSheet({
  open,
  user,
  busy,
  onClose,
  onSave,
}: {
  open: boolean;
  user: User | null;
  busy: boolean;
  onClose: () => void;
  onSave: (input: UserForm) => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<UserForm>(() => blank());
  const [seeded, setSeeded] = useState<string | null>(null);

  // Seed the form once per open, keyed on the user being edited.
  const key = open ? (user?.id ?? 'novo') : null;
  if (key && key !== seeded) {
    setSeeded(key);
    setForm(
      user
        ? {
            displayName: user.displayName,
            username: user.username,
            role: user.role,
            numeroMecanografico: user.numeroMecanografico,
            categoria: user.categoria,
            email: user.email,
            phoneNumber: user.phoneNumber,
            naEmpresaDesde: user.naEmpresaDesde,
            horasAlvoDiarias: user.horasAlvoDiarias,
            ativo: user.ativo,
          }
        : blank(),
    );
  }
  if (!open && seeded) setSeeded(null);

  const set = <K extends keyof UserForm>(field: K, value: UserForm[K]) =>
    setForm((f) => ({ ...f, [field]: value }));

  const texto = (
    label: string,
    field: keyof UserForm,
    props: { placeholder?: string; inputMode?: 'numeric' | 'tel' | 'email' } = {},
  ) => (
    <div>
      <div className="label-eyebrow">{label}</div>
      <input
        className="field mt-2 bg-[#fbfbfd] text-[13px]"
        value={(form[field] as string | null) ?? ''}
        inputMode={props.inputMode}
        placeholder={props.placeholder}
        onChange={(e) => set(field, (e.target.value || null) as UserForm[keyof UserForm])}
      />
    </div>
  );

  return (
    <Sheet open={open} onClose={onClose} title={user ? t('common.edit') : t('users.novo')}>
      <div className="flex flex-col gap-3.5">
        <div>
          <div className="label-eyebrow">{t('users.nome')}</div>
          <input
            className="field mt-2 bg-[#fbfbfd] text-[13px]"
            value={form.displayName}
            onChange={(e) => set('displayName', e.target.value)}
          />
        </div>

        {!user && (
          <>
            <div>
              <div className="label-eyebrow">{t('users.username')}</div>
              <input
                className="field mt-2 bg-[#fbfbfd] text-[13px]"
                autoCapitalize="none"
                spellCheck={false}
                placeholder={t('users.usernameHint')}
                value={form.username}
                onChange={(e) => set('username', e.target.value.toLowerCase().replace(/\s/g, ''))}
              />
            </div>
            <div>
              <div className="label-eyebrow">{t('users.codigo')}</div>
              <input
                className="field num mt-2 bg-[#fbfbfd] text-[13px] tracking-widest"
                inputMode="numeric"
                placeholder="1234"
                value={form.password ?? ''}
                onChange={(e) => set('password', e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </div>
          </>
        )}

        <div>
          <div className="label-eyebrow">{t('users.perfil')}</div>
          <div className="mt-2 flex flex-col gap-1.5">
            {ROLES.map((role) => {
              const ativo = form.role === role;
              return (
                <button
                  key={role}
                  type="button"
                  onClick={() => set('role', role)}
                  className={`flex items-center justify-between gap-2.5 rounded-xl border px-3.5 py-3 text-left transition ${
                    ativo ? 'border-conduril bg-[#f2f4fb]' : 'border-conduril-border bg-white'
                  }`}
                >
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-[13px] font-semibold text-conduril-ink">
                      {t(`users.role${role}`)}
                    </span>
                    <span className="text-[10px] text-conduril-muted">{t(`users.role${role}Sub`)}</span>
                  </span>
                  <span
                    className={`h-4 w-4 shrink-0 rounded-full border-2 ${
                      ativo ? 'border-conduril bg-conduril' : 'border-[#ccd1dd] bg-white'
                    }`}
                  />
                </button>
              );
            })}
          </div>
        </div>

        {texto(t('users.numero'), 'numeroMecanografico', { placeholder: '4471', inputMode: 'numeric' })}
        {texto(t('users.categoria'), 'categoria', { placeholder: 'Eletricista oficial' })}
        {texto(t('users.telemovel'), 'phoneNumber', { inputMode: 'tel' })}
        {texto(t('users.email'), 'email', { inputMode: 'email' })}
        {texto(t('users.desde'), 'naEmpresaDesde', { placeholder: 'Março 2021' })}

        <div>
          <div className="label-eyebrow">{t('users.horasAlvo')}</div>
          <input
            className="field num mt-2 bg-[#fbfbfd] text-[13px]"
            inputMode="numeric"
            placeholder="8"
            value={form.horasAlvoDiarias ?? ''}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^\d.,]/g, '').replace(',', '.');
              set('horasAlvoDiarias', raw ? Number(raw) : null);
            }}
          />
        </div>

        {user && (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-conduril-border bg-white px-3.5 py-3">
            <span className="text-[13px] font-semibold text-conduril-ink">{t('users.ativo')}</span>
            <Toggle checked={form.ativo} onChange={(v) => set('ativo', v)} label={t('users.ativo')} />
          </div>
        )}
      </div>

      <button
        type="button"
        disabled={busy || !form.displayName.trim() || (!user && form.username.trim().length < 2)}
        onClick={() => onSave(form)}
        className="btn-primary mt-5"
      >
        {user ? t('common.save') : t('users.criar')}
      </button>
    </Sheet>
  );
}

function blank(): UserForm {
  return {
    displayName: '',
    username: '',
    password: '',
    role: 'Eletricista',
    numeroMecanografico: null,
    categoria: null,
    email: null,
    phoneNumber: null,
    naEmpresaDesde: null,
    horasAlvoDiarias: null,
    ativo: true,
  };
}
